import { useState, useRef, useEffect, useCallback } from "react";
import { apiFetch } from "../../../api";
import { useAiChat } from "../learning/AiChatContext";
import { AiSparkleIcon } from "../learning/components/AiChatPopup";
import s from "./SentenceReviewCard.module.css";
const ENGLISH_FRONT_KEY = "sentenceReview.englishFront";
const AUTO_PLAY_KEY = "sentenceReview.autoPlay";
const HIDE_FRONT_KEY = "sentenceReview.hideFront";

const readBool = (key, fallback = false) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === "true";
  } catch { return fallback; }
};
const writeBool = (key, val) => {
  try { localStorage.setItem(key, val ? "true" : "false"); } catch {}
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function PlayIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
    </svg>
  );
}

function EyeIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
      <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

export default function SentenceReviewCard({ sentences: sentencesProp, onBack, onComplete, onAskAi, embedded, onStateChange }) {
  const { setModuleContext } = useAiChat();
  const [items] = useState(() => shuffle(sentencesProp));
  const [status, setStatus] = useState("idle");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [frontRevealed, setFrontRevealed] = useState(false);
  const [results, setResults] = useState([]);
  const [animating, setAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null);

  // Settings
  const [englishFront, setEnglishFront] = useState(() => readBool(ENGLISH_FRONT_KEY));
  const [autoPlay, setAutoPlay] = useState(() => readBool(AUTO_PLAY_KEY, true));
  const [hideFront, setHideFront] = useState(() => readBool(HIDE_FRONT_KEY));
  const [showSettings, setShowSettings] = useState(false);

  const audioRef = useRef(null);
  const ttsTimerRef = useRef(null);

  const total = items.length;
  const current = items[currentIndex] || null;

  // Derive card content
  const getCardContent = useCallback((item) => {
    if (!item) return { frontText: "", backText: "", frontLang: "sv", backLang: "en" };
    if (englishFront) {
      return { frontText: item.in_english, backText: item.in_swedish, frontLang: "en", backLang: "sv" };
    }
    return { frontText: item.in_swedish, backText: item.in_english, frontLang: "sv", backLang: "en" };
  }, [englishFront]);

  // AI context
  useEffect(() => {
    if (!current) return;
    const { frontText, frontLang } = getCardContent(current);
    setModuleContext({
      origin: "sentence_review",
      sentence: {
        in_swedish: current.in_swedish,
        in_english: current.in_english,
      },
      card_front: frontText,
      card_front_language: frontLang === "en" ? "English" : "Swedish",
    });
  }, [current, getCardContent, setModuleContext]);

  // Play Swedish audio via backend
  const playSwedishAudio = useCallback((sentenceId) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const audio = new Audio();
    apiFetch(`/api/v1/sentences/${sentenceId}/audio`).then(async (res) => {
      if (res.redirected) {
        audio.src = res.url;
      } else {
        const blob = await res.blob();
        audio.src = URL.createObjectURL(blob);
      }
      audio.play().catch(() => {});
    }).catch(() => {});
    audioRef.current = audio;
  }, []);

  // Play English TTS
  const playEnglishTts = useCallback((text) => {
    if (!text || !window.speechSynthesis) return;
    clearTimeout(ttsTimerRef.current);
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = "en-US";
    utt.rate = 0.9;
    ttsTimerRef.current = setTimeout(() => window.speechSynthesis.speak(utt), 50);
  }, []);

  const stopAllAudio = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    clearTimeout(ttsTimerRef.current);
    window.speechSynthesis?.cancel();
  }, []);

  // Auto-play
  useEffect(() => {
    if (status !== "playing" || !current) return;
    if (!autoPlay) { stopAllAudio(); return; }
    const { frontLang, backLang } = getCardContent(current);
    const visibleLang = !flipped ? frontLang : backLang;

    if (visibleLang === "sv") {
      window.speechSynthesis?.cancel();
      playSwedishAudio(current.id);
    } else {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      const { frontText, backText } = getCardContent(current);
      playEnglishTts(!flipped ? frontText : backText);
    }
  }, [status, currentIndex, flipped, current, autoPlay, getCardContent, playSwedishAudio, playEnglishTts, stopAllAudio]);

  // Cleanup
  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
    clearTimeout(ttsTimerRef.current);
    window.speechSynthesis?.cancel();
  }, []);

  // Report state to parent
  useEffect(() => {
    onStateChange?.({ status, currentIndex, total });
  }, [status, currentIndex, total, onStateChange]);

  // Settings handlers
  const handleToggleEnFront = useCallback(() => {
    setEnglishFront((v) => { const n = !v; writeBool(ENGLISH_FRONT_KEY, n); return n; });
  }, []);
  const handleToggleAutoPlay = useCallback(() => {
    setAutoPlay((v) => {
      const n = !v;
      writeBool(AUTO_PLAY_KEY, n);
      if (!n) { setHideFront(false); writeBool(HIDE_FRONT_KEY, false); }
      return n;
    });
  }, []);
  const handleToggleHideFront = useCallback(() => {
    setHideFront((v) => { const n = !v; writeBool(HIDE_FRONT_KEY, n); return n; });
  }, []);

  // Submit response and advance
  const advance = useCallback((responseType) => {
    if (animating || !current) return;
    stopAllAudio();

    const newResults = [...results, { sentenceId: current.id, responseType }];
    setResults(newResults);

    // Fire-and-forget API call
    apiFetch(`/api/v1/sentences/${current.id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ response_type: responseType }),
    }).catch(() => {});

    if (currentIndex >= total - 1) {
      setStatus("done");
      return;
    }

    setSlideDirection("left");
    setAnimating(true);
    setTimeout(() => {
      setCurrentIndex((i) => i + 1);
      setFlipped(false);
      setFrontRevealed(false);
      setAnimating(false);
    }, 200);
  }, [animating, results, current, currentIndex, total, stopAllAudio]);

  const simpleCount = results.filter((r) => r.responseType === "simple").length;
  const hardCount = results.filter((r) => r.responseType === "hard").length;
  const wrongCount = results.filter((r) => r.responseType === "wrong").length;

  const slideClass = animating
    ? s.slideOutLeft
    : (slideDirection ? s.slideInRight : s.slideInInitial);

  const Header = (
    <div className={s.header}>
      <button className={s.backBtn} onClick={onBack} title="Back">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <div className={s.headerIcon}>
        <svg className={s.headerIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3M12.334 5.364a48.11 48.11 0 0 1 3.413.263" />
        </svg>
      </div>
      <h2 className={s.headerTitle}>Sentence Review</h2>
      {onAskAi && (
        <button className={s.aiBtn} onClick={onAskAi} aria-label="Ask AI tutor">
          <AiSparkleIcon className={s.aiBtnIcon} />
          <span className={s.aiBtnLabel}>AI</span>
        </button>
      )}
    </div>
  );

  if (total === 0) {
    const inner = (
      <div className={s.content}>
        <div className={s.emptyState}>
          <p>No sentences to review right now!</p>
          <button className={s.doneCloseBtn} onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
    return embedded ? inner : <div className={s.panel}>{Header}{inner}</div>;
  }

  /* ── Idle: start screen with settings ── */
  if (status === "idle") {
    const inner = (
      <div className={s.content}>
        <div className={s.startScreen}>
            <div className={s.startHero}>
              <div className={s.startIconRing}>
                <div className={s.startIconInner}>
                  <svg className={s.startIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3M12.334 5.364a48.11 48.11 0 0 1 3.413.263" />
                  </svg>
                </div>
              </div>
              <h3 className={s.startTitle}>Ready to review?</h3>
              <p className={s.startDesc}>{total} sentence{total !== 1 ? "s" : ""} due for review</p>
            </div>

            <button className={s.startBtn} onClick={() => setStatus("playing")}>
              <PlayIcon className={s.startBtnIcon} />
              <span>Start Review</span>
            </button>

            {/* Settings toggle */}
            <button
              className={`${s.configToggleBtn} ${showSettings ? s.configToggleBtnOpen : ""}`}
              onClick={() => setShowSettings((v) => !v)}
            >
              <svg className={s.configToggleIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992.014.399.014.798 0 1.197-.007.379.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.248a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-1.197c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.248a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
              <span>Settings</span>
              <svg className={`${s.configToggleChevron} ${showSettings ? s.configToggleChevronOpen : ""}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {showSettings && (
            <div className={s.configList}>
              <label className={s.configRow}>
                <div className={s.configText}>
                  <span className={s.configTitle}>
                    {englishFront ? "English front" : "Swedish front"}
                  </span>
                  <span className={s.configDesc}>
                    {englishFront
                      ? "See English first. Tap Show Response for Swedish."
                      : "See Swedish first. Tap Show Response for English."}
                  </span>
                </div>
                <span
                  className={`${s.switch} ${englishFront ? s.switchOn : ""}`}
                  onClick={(e) => { e.preventDefault(); handleToggleEnFront(); }}
                  role="switch"
                  aria-checked={englishFront}
                >
                  <span className={s.switchKnob} />
                </span>
              </label>

              <label className={s.configRow}>
                <div className={s.configText}>
                  <span className={s.configTitle}>Auto play audio</span>
                  <span className={s.configDesc}>Play audio when each card side is shown.</span>
                </div>
                <span
                  className={`${s.switch} ${autoPlay ? s.switchOn : ""}`}
                  onClick={(e) => { e.preventDefault(); handleToggleAutoPlay(); }}
                  role="switch"
                  aria-checked={autoPlay}
                >
                  <span className={s.switchKnob} />
                </span>
              </label>

              <label className={`${s.configRow} ${!autoPlay ? s.configRowDisabled : ""}`}>
                <div className={s.configText}>
                  <span className={s.configTitle}>Hide front text</span>
                  <span className={s.configDesc}>Blur the front side until you tap to reveal.</span>
                </div>
                <span
                  className={`${s.switch} ${hideFront && autoPlay ? s.switchOn : ""} ${!autoPlay ? s.switchDisabled : ""}`}
                  onClick={(e) => { e.preventDefault(); if (autoPlay) handleToggleHideFront(); }}
                  role="switch"
                  aria-checked={hideFront && autoPlay}
                >
                  <span className={s.switchKnob} />
                </span>
              </label>
            </div>
            )}
          </div>
        </div>
    );
    return embedded ? inner : <div className={s.panel}>{Header}{inner}</div>;
  }

  /* ── Done screen ── */
  if (status === "done") {
    const inner = (
      <div className={s.content}>
        <div className={s.doneScreen}>
            <div className={s.doneIcon}>
              <svg className={s.doneIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>

            <h3 className={s.doneTitle}>Review Complete!</h3>

            <div className={s.doneStats}>
              <div className={`${s.doneStat} ${s.doneStatSimple}`}>
                <span className={s.doneStatNum}>{simpleCount}</span>
                <span className={s.doneStatLabel}>Got it</span>
              </div>
              <div className={s.doneStatDivider} />
              <div className={`${s.doneStat} ${s.doneStatHard}`}>
                <span className={s.doneStatNum}>{hardCount}</span>
                <span className={s.doneStatLabel}>Hard</span>
              </div>
              <div className={s.doneStatDivider} />
              <div className={`${s.doneStat} ${s.doneStatWrong}`}>
                <span className={s.doneStatNum}>{wrongCount}</span>
                <span className={s.doneStatLabel}>Wrong</span>
              </div>
            </div>

            <p className={s.doneDesc}>
              {wrongCount === 0
                ? "Perfect session! All sentences scheduled for later review."
                : `${wrongCount} sentence${wrongCount !== 1 ? "s" : ""} will come back sooner for extra practice.`}
            </p>

            <button className={s.doneCloseBtn} onClick={onComplete}>
              Done
            </button>
          </div>
        </div>
    );
    return embedded ? inner : <div className={s.panel}>{Header}{inner}</div>;
  }

  /* ── Playing ── */
  const { frontText, backText, frontLang, backLang } = getCardContent(current);
  const isBlurred = hideFront && autoPlay && !frontRevealed && !flipped;

  const renderFace = (lang, text, blurred, showFlipBack) => {
    const isSv = lang === "sv";
    return (
      <div className={s.flashcardFace}>
        <span className={`${s.flashcardBadge} ${isSv ? "" : s.flashcardBadgeEn}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            {isSv ? (
              <path d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0L12 17.25 6.429 14.25m11.142 0L21.75 16.5 12 21.75 2.25 16.5l4.179-2.25" />
            ) : (
              <path d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3M12.334 5.364a48.11 48.11 0 0 1 3.413.263M15.747 5.627a40.42 40.42 0 0 1-3.413-.263m0 0A42.18 42.18 0 0 0 9 5.25m3.334.114A49.5 49.5 0 0 1 15 9.75" />
            )}
          </svg>
          <span>{isSv ? "Swedish" : "English"}</span>
        </span>

        <p className={`${s.flashcardText} ${blurred ? s.flashcardTextBlurred : ""}`}>{text}</p>

        <div className={s.faceActions}>
          {blurred && (
            <button type="button" className={s.revealToggleBtn} onClick={() => setFrontRevealed(true)}>
              <EyeIcon className={s.revealToggleIcon} />
              <span>Reveal text</span>
            </button>
          )}
          {!blurred && hideFront && autoPlay && !flipped && (
            <button type="button" className={s.revealToggleBtn} onClick={() => setFrontRevealed(false)}>
              <EyeIcon className={s.revealToggleIcon} />
              <span>Hide text</span>
            </button>
          )}
          {isSv && (
            <button className={s.facePlayBtn} onClick={() => playSwedishAudio(current.id)} title="Play Swedish audio">
              <PlayIcon className={s.facePlayIcon} />
              <span>Play</span>
            </button>
          )}
          {!isSv && (
            <button className={s.facePlayBtn} onClick={() => playEnglishTts(text)} title="Play English audio">
              <PlayIcon className={s.facePlayIcon} />
              <span>Play</span>
            </button>
          )}
          {showFlipBack && (
            <button className={s.faceFlipBackBtn} onClick={() => setFlipped(false)} title="Flip back">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
              </svg>
              <span>Flip back</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  const inner = (
    <div className={s.content}>
      <div className={`${s.cardWrap} ${slideClass}`} key={currentIndex}>
        <div className={s.flashcard}>
          {!flipped
            ? renderFace(frontLang, frontText, isBlurred, false)
            : renderFace(backLang, backText, false, true)}
        </div>

        {!flipped ? (
          <button className={s.showResponseBtn} onClick={() => setFlipped(true)}>
            <span>Show Response</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
            </svg>
          </button>
        ) : (
          <div className={s.actions}>
            <button className={s.wrongBtn} onClick={() => advance("wrong")}>
              <svg className={s.actionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 18 18 6M6 6l12 12" />
              </svg>
              <span>Wrong</span>
            </button>
            <button className={s.hardBtn} onClick={() => advance("hard")}>
              <svg className={s.actionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
              <span>Hard</span>
            </button>
            <button className={s.simpleBtn} onClick={() => advance("simple")}>
              <svg className={s.actionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>Got it</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) return inner;

  return (
    <div className={s.panel}>
      <div className={s.topBar}>
        <button className={s.closeBtn} onClick={onBack} title="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <div className={s.topProgressBar}>
          <div className={s.topProgressFill} style={{ width: `${((currentIndex + 1) / total) * 100}%` }} />
        </div>
        {onAskAi && (
          <button className={s.aiBtn} onClick={onAskAi} aria-label="Ask AI tutor">
            <AiSparkleIcon className={s.aiBtnIcon} />
            <span className={s.aiBtnLabel}>AI</span>
          </button>
        )}
      </div>
      {inner}
    </div>
  );
}
